/**
 * ═══════════════════════════════════════════════════════════════════
 * points-de-ventes.js — Module de géolocalisation Floatee
 * LECTURE TECHNIQUE TOP-DOWN (Logique structurelle)
 *
 * Ce module est agnostique du JSON source : il accepte n'importe quel
 * objet JSON dont la première clé de type tableau contient les fiches
 * à afficher (ex : { "revendeurs": [...] } ou { "maitres-nageurs": [...] }).
 * Chaque fiche peut définir tel ou tel champ (tel, mail, url, tag…) :
 * seuls les champs présents sont affichés.
 * ═══════════════════════════════════════════════════════════════════
 */

(function () {
    'use strict';

    /* ── Configurations et constantes ────────────────────────────── */
    const CLUSTER_RADIUS = 15;
    const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const TILE_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
    const COULEUR_DEFAUT = '#0f315e';

    /* ── État de l'application (Variables globales) ──────────────── */
    let srcData = [];
    let filtreIndex = {};
    let activeFilter = 'all';
    let searchQuery = '';

    /**
     * TEXTES — alimenté par chargerConfigurationTextes() depuis le JSON
     * inline injecté par Liquid. Toutes les chaînes visibles par
     * l'utilisateur passent par ici : jamais codées en dur dans le JS.
     * Valeurs de secours (fallback) définies ici au cas où le JSON
     * serait absent.
     */
    let TEXTES = {
        tous_revendeurs: 'Tous les revendeurs',
        recherche_revendeurs: 'Recherche en cours…',
        revendeurs_trouves: 'revendeurs trouvés',
        bouton_carte: 'Afficher la carte',
        autorisation_js: 'Activez JavaScript pour afficher la liste.',
        ctrl_zoom: 'Ctrl + Molette pour zoomer',
        erreur_chargement: 'Les données n\'ont pas pu être chargées.',
        vous_etes_ici: '📍 Vous êtes ici',
        geoloc_non_supportee: 'La géolocalisation n\'est pas supportée par votre navigateur.',
        geoloc_erreur: 'Impossible d\'accéder à votre position actuelle.',
    };

    let map;
    let clusterGroup;
    let markersById = {};
    let mapLoaded = false;

    /* ── POINT D'ENTRÉE ──────────────────────────────────────────── */
    document.addEventListener('DOMContentLoaded', initApp);

    /**
     * UTILITÉ : Initialise l'application dès que le DOM est prêt.
     * Elle orchestre de manière descendante le chargement des données,
     * la construction de la sidebar et l'attribution des écouteurs.
     */
    async function initApp() {
        const rootContainer = document.getElementById('carteInteractive');
        if (!rootContainer) return;

        // 1a. Chargement des textes éditables depuis le schema Liquid
        chargerConfigurationTextes();

        // 1b. Extraction de la configuration des blocs Shopify (gammes)
        chargerConfigurationFiltres();

        // 2. Récupération distante des données (revendeurs, moniteurs…)
        const storesUrl = rootContainer.dataset.storesUrl;
        if (!storesUrl) return;

        try {
            const res = await fetch(storesUrl);
            if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
            const json = await res.json();
            srcData = extraireTableauSource(json);
        } catch (err) {
            console.error('[StoreLocator] Fail to fetch selling point', err);
            afficherMessageErreur();
            return;
        }

        // 3. Construction immédiate de l'interface textuelle
        construireSidebarTextuelle();

        // 4. Attachement global des écouteurs d'événements
        brancherFiltres();
        brancherRecherche();
        brancherLazyMap(rootContainer);
        brancherGeolocalisation();

        // 5. Rendu initial pour appliquer les filtres par défaut
        filtrerEtMettreAJour();
    }

    /* ── FONCTIONS D'INITIALISATION DE L'APPLICATION ──────────────── */

    /**
     * UTILITÉ : Rend le module agnostique de la forme exacte du JSON source.
     * Accepte directement un tableau, ou un objet dont l'une des clés
     * contient un tableau (peu importe son nom : "revendeurs",
     * "maitres-nageurs", "srcData", etc.).
     */
    function extraireTableauSource(json) {
        if (Array.isArray(json)) return json;
        if (json && typeof json === 'object') {
            const premierTableau = Object.values(json).find(valeur => Array.isArray(valeur));
            if (premierTableau) return premierTableau;
        }
        return [];
    }

    /**
     * UTILITÉ : Lit le bloc <script id="textes-json-data"> injecté par Liquid
     * et écrase les valeurs par défaut de TEXTES avec celles du schema.
     * Si le bloc est absent (test hors Shopify), les fallbacks restent actifs.
     */
    function chargerConfigurationTextes() {
        const el = document.getElementById('textes-json-data');
        if (!el) return;
        try {
            const valeurs = JSON.parse(el.textContent);
            // Fusion : on ne remplace que les clés présentes, les fallbacks restent pour les clés manquantes
            TEXTES = Object.assign(TEXTES, valeurs);
        } catch (e) {
            console.error('[StoreLocator] JSON textes invalide', e);
        }
    }

    /**
     * UTILITÉ : Extrait et mémorise les couleurs et libellés des gammes
     * générés dans le bloc d'application JSON du fichier Liquid.
     */
    function chargerConfigurationFiltres() {
        const dataContainer = document.getElementById('gammes-json-data');
        if (!dataContainer) return;
        try {
            const liste = JSON.parse(dataContainer.textContent);
            liste.forEach(g => { filtreIndex[g.handle] = g; });
        } catch (e) {
            console.error('[StoreLocator] JSON Filtres introuvables', e);
        }
    }

    /**
     * UTILITÉ : Génère et injecte dynamiquement les éléments <li> de la liste
     * des fiches dans la sidebar. Utilise un fragment pour optimiser les performances du DOM.
     */
    function construireSidebarTextuelle() {
        const listWrapper = document.getElementById('store-list');
        if (!listWrapper) return;

        const fragment = document.createDocumentFragment();

        srcData.forEach(r => {
            const li = document.createElement('li');
            li.className = 'store-list-item';
            li.dataset.id = r.id;
            li.dataset.ville = (r.ville || '').toLowerCase();
            li.dataset.filtres = (r.filtres || []).join(',');

            li.innerHTML = genererCardHTML(r);
            fragment.appendChild(li);
        });

        listWrapper.innerHTML = '';
        listWrapper.appendChild(fragment);

        // Écouteur délégué : gère le clic sur n'importe quelle carte de la liste
        listWrapper.addEventListener('click', e => {
            const card = e.target.closest('.store-card');
            if (!card) return;

            const id = card.dataset.id;
            if (!mapLoaded) {
                chargerCarteInteractive();
                // Laisse 500ms à Leaflet pour calculer ses dimensions avant de cibler le point
                setTimeout(() => centrerEtOuvrirMarqueur(id), 500);
            } else {
                centrerEtOuvrirMarqueur(id);
            }
        });
    }

    /* ── FONCTIONS DE LOGIQUE MÉTIER & FILTRAGE ──────────────────── */

    /**
     * UTILITÉ : Associe les clics sur les boutons de type "Pastille" (chips)
     * pour mémoriser la gamme active et déclencher le recalcul d'affichage.
     */
    function brancherFiltres() {
        document.querySelectorAll('.chip').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.chip').forEach(b => {
                    b.classList.remove('chip--active');
                    b.setAttribute('aria-pressed', 'false');
                });
                btn.classList.add('chip--active');
                btn.setAttribute('aria-pressed', 'true');

                activeFilter = btn.dataset.filter;
                filtrerEtMettreAJour();
            });
        });
    }

    /**
     * UTILITÉ : Capte les saisies de caractères dans le champ de recherche textuelle
     * pour mettre à jour la variable globale et lancer le filtrage.
     */
    function brancherRecherche() {
        const inputField = document.getElementById('search-field');
        if (!inputField) return;
        inputField.addEventListener('input', e => {
            searchQuery = e.target.value.toLowerCase().trim();
            filtrerEtMettreAJour();
        });
    }

    /**
     * UTILITÉ : Fonction pivot qui croise les critères du filtre de gamme et de
     * la recherche textuelle. Modifie l'attribut `hidden` des éléments HTML.
     */
    function filtrerEtMettreAJour() {
        let nombreVisibles = 0;

        document.querySelectorAll('.store-list-item').forEach(li => {
            const filtresDispo = (li.dataset.filtres || '').split(',').filter(Boolean);
            const nomVille = li.dataset.ville || '';
            const nomFiche = li.querySelector('.store-card__name')?.textContent.toLowerCase() || '';

            const correspondanceFiltre = activeFilter === 'all' || filtresDispo.includes(activeFilter);
            const correspondanceRecherche = searchQuery === '' || nomVille.includes(searchQuery) || nomFiche.includes(searchQuery);

            const doitEtreAffiche = correspondanceFiltre && correspondanceRecherche;

            li.hidden = !doitEtreAffiche;
            if (doitEtreAffiche) nombreVisibles++;
        });

        actualiserCompteurTextuel(nombreVisibles);
        rafraichirVisibiliteMarqueursCarte();
    }

    /**
     * UTILITÉ : Modifie le contenu textuel de l'indicateur du nombre de résultats
     * en prenant en compte la gestion du pluriel.
     */
    function actualiserCompteurTextuel(n) {
        const counterEl = document.getElementById('results-counter');
        if (counterEl) {
            counterEl.textContent = n > 0
                ? `${n} ${TEXTES.revendeurs_trouves}`
                : "";
        }
    }

    /* ── FONCTIONS D'INTERACTION AVEC LA CARTE ────────────────────── */

    /**
     * UTILITÉ : Attache les gestionnaires de clic sur l'overlay d'attente de
     * la carte pour instancier la carte Leaflet uniquement à la demande.
     */
    function brancherLazyMap(rootContainer) {
        document.getElementById('map-overlay')?.addEventListener('click', chargerCarteInteractive);
        document.getElementById('map-trigger-button')?.addEventListener('click', e => {
            e.stopPropagation();
            chargerCarteInteractive();
        });
    }

    /**
     * UTILITÉ : Fonction globale intermédiaire appelée pour instancier la carte interactive
     * (utilisée notamment lors d'un clic sur une fiche de la sidebar avant que la carte n'existe).
     */
    function chargerCarteInteractive() {
        const rootContainer = document.getElementById('carteInteractive');
        if (rootContainer) initConfigurationCarte(rootContainer);
    }

    /**
     * UTILITÉ : Crée l'instance de la carte Leaflet, configure les calques d'imagerie,
     * génère les marqueurs SVG individuels et initialise le système de clusters.
     */
    function initConfigurationCarte(rootContainer) {
        if (mapLoaded || typeof L === 'undefined') return;
        mapLoaded = true;

        // Mutation de l'affichage des conteneurs
        document.getElementById('map-overlay').style.display = 'none';
        const mapContainer = document.getElementById('map-container');
        mapContainer.style.display = 'block';

        const zoom = parseFloat(rootContainer.dataset.zoom) || 6;
        const lat = parseFloat(rootContainer.dataset.lat) || 46.8;
        const lng = parseFloat(rootContainer.dataset.lng) || 1.8;

        // Création de l'objet Map sur l'ID conteneur harmonisé
        map = L.map('map-container', { scrollWheelZoom: false }).setView([lat, lng], zoom);

        L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map);

        clusterGroup = L.markerClusterGroup({
            maxClusterRadius: CLUSTER_RADIUS,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            iconCreateFunction: genererStyleIconeCluster,
        });

        map.getContainer().addEventListener('wheel', function (event) {
            if (event.ctrlKey) {
                event.preventDefault();

                if (event.deltaY < 0) {
                    map.zoomIn();
                } else {
                    map.zoomOut();
                }
            }
        }, { passive: false });

        // Rendu et stockage des marqueurs
        srcData.forEach(r => {
            // Ignore les fiches sans coordonnées GPS valides (ex : listing sans carte)
            if (!r.lat || !r.lng || isNaN(parseFloat(r.lat)) || isNaN(parseFloat(r.lng))) return;

            const couleur = (r.filtres && r.filtres.length > 0)
                ? (filtreIndex[r.filtres[0]]?.couleur || COULEUR_DEFAUT)
                : COULEUR_DEFAUT;

            const CustomHtmlIcon = L.divIcon({
                className: '',
                html: genererIconeSvgMarqueur(couleur),
                iconSize: [28, 38],
                iconAnchor: [14, 38],
                popupAnchor: [0, -42],
            });

            // parseFloat() corrige le cas où lat/lng sont des strings dans le JSON
            const marker = L.marker([parseFloat(r.lat), parseFloat(r.lng)], { icon: CustomHtmlIcon })
                .bindPopup(L.popup({ maxWidth: 300, className: 'store-popup' })
                    .setContent(() => genererEncapsulationPopup(r)));

            marker.on('click', () => appliquerSurlignageSidebar(r.id));

            markersById[r.id] = marker;
            clusterGroup.addLayer(marker);
        });

        map.addLayer(clusterGroup);
        rafraichirVisibiliteMarqueursCarte();
    }

    /**
     * CRITIQUE : C'est ici que se réglait le bug. Elle lit `.store-list-item:not([hidden])`
     * pour savoir exactement quels marqueurs ajouter au groupe de clusters Leaflet.
     */
    function rafraichirVisibiliteMarqueursCarte() {
        if (!mapLoaded || !clusterGroup) return;
        clusterGroup.clearLayers();

        document.querySelectorAll('.store-list-item:not([hidden])').forEach(li => {
            const markerInstance = markersById[li.dataset.id];
            if (markerInstance) clusterGroup.addLayer(markerInstance);
        });
    }

    /**
     * UTILITÉ : Centre la carte sur un marqueur précis, ouvre sa bulle popup
     * d'information et applique le style d'activation sur l'élément de la sidebar.
     */
    function centrerEtOuvrirMarqueur(id) {
        const fiche = srcData.find(x => x.id === id);
        if (!fiche || !markersById[id]) return;
        map.setView([parseFloat(fiche.lat), parseFloat(fiche.lng)], 14);
        markersById[id].openPopup();
        appliquerSurlignageSidebar(id);
    }

    /**
     * UTILITÉ : Ajoute une classe CSS active sur la fiche sélectionnée et fait
     * défiler le scroll de la liste pour placer l'élément au centre du regard.
     */
    function appliquerSurlignageSidebar(id) {
        document.querySelectorAll('.store-card').forEach(c =>
            c.classList.toggle('store-card--active', c.dataset.id === id)
        );
        const carteCible = document.querySelector(`.store-card[data-id="${id}"]`);
        if (carteCible) {
            carteCible.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    /**
     * UTILITÉ : Intercepte les requêtes de géolocalisation de l'API de navigation
     * HTML5 pour positionner un indicateur circulaire spécial à l'emplacement de l'utilisateur.
     */
    function brancherGeolocalisation() {
        const geoBtn = document.getElementById('geolocate-button');
        if (!geoBtn) return;

        geoBtn.addEventListener('click', () => {
            if (!navigator.geolocation) {
                alert(TEXTES.geoloc_non_supportee);
                return;
            }

            geoBtn.disabled = true;

            navigator.geolocation.getCurrentPosition(
                position => {
                    const { latitude, longitude } = position.coords;
                    if (!mapLoaded) chargerCarteInteractive();

                    setTimeout(() => {
                        map.setView([latitude, longitude], 12);
                        L.circleMarker([latitude, longitude], {
                            radius: 9, color: COULEUR_DEFAUT, fillColor: COULEUR_DEFAUT,
                            fillOpacity: 1, weight: 2,
                        }).addTo(map).bindPopup(TEXTES.vous_etes_ici).openPopup();
                    }, mapLoaded ? 0 : 500);

                    geoBtn.disabled = false;
                },
                () => {
                    alert(TEXTES.geoloc_erreur);
                    geoBtn.disabled = false;
                }
            );
        });
    }

    /* ── UTILITAIRES & RENDER HELPERS (Bas de page) ──────────────── */

    /**
     * UTILITÉ : Découpe un champ "tag" en items individuels. Tolère un tableau
     * de plusieurs libellés, ou un tableau à une seule entrée où les libellés
     * sont concaténés avec des "•" ou des virgules (ex : cve_fnmns.json).
     */
    function normaliserTags(tags) {
        if (!Array.isArray(tags)) return [];
        return tags
            .flatMap(t => String(t).split(/[•,]/))
            .map(t => t.trim())
            .filter(Boolean);
    }

    /**
     * UTILITÉ : Génère une fiche descriptive unifiée (utilisée pour injecter le code
     * HTML dans les balises de la sidebar ainsi que dans les fenêtres popups Leaflet).
     */
    function genererCardHTML(r) {
        const badgesHtml = (r.filtres || [])
            .map(handle => {
                const g = filtreIndex[handle];
                if (!g) return '';
                return `<li class="card-chip" style="--badge-color:${g.couleur || COULEUR_DEFAUT}">${escapeHtml(g.label)}</li>`;
            })
            .join('');

        const tagsHtml = normaliserTags(r.tag)
            .map(t => `<li class="info-chip">${escapeHtml(t)}</li>`)
            .join('');

        const queryAdresse = encodeURIComponent(`${r.label || ''} ${r.adresse || ''} ${r.cp || ''} ${r.ville || ''}`);
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${queryAdresse}`;

        const modePref = (r.mode_contact_pref || '').toLowerCase();
        const classeTel = modePref === 'tel' ? ' store-card__link--preferred' : '';
        const classeMail = modePref === 'email' || modePref === 'mail' ? ' store-card__link--preferred' : '';

        return `
      <article class="store-card" data-id="${escapeAttr(r.id)}">
        <header class="store-card__header">
          <h3 class="store-card__name">${escapeHtml(r.label)}</h3>
          <address class="store-card__address">
            ${escapeHtml(r.adresse)},<br>${escapeHtml(r.cp)}, ${escapeHtml(r.ville)}<br>${escapeHtml(r.pays)}
          </address>
        </header>
        ${badgesHtml ? `<ul class="card-list-chip" aria-label="Gammes disponibles">${badgesHtml}</ul>` : ''}
        ${tagsHtml ? `<ul class="card-list-info" aria-label="Informations complémentaires">${tagsHtml}</ul>` : ''}
        <footer class="store-card__footer">
          <a class="store-card__link itineraire" href="${mapsUrl}" target="_blank" rel="noopener noreferrer"><svg width="16" height="16" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.7996 4.82957L4.82957 21.7996L21.7996 38.7696L38.7696 21.7996L21.7996 4.82957ZM23.2136 0.585573L43.0136 20.3856C43.3885 20.7606 43.5991 21.2692 43.5991 21.7996C43.5991 22.3299 43.3885 22.8385 43.0136 23.2136L23.2136 43.0136C22.8385 43.3885 22.3299 43.5991 21.7996 43.5991C21.2692 43.5991 20.7606 43.3885 20.3856 43.0136L0.585573 23.2136C0.210631 22.8385 0 22.3299 0 21.7996C0 21.2692 0.210631 20.7606 0.585573 20.3856L20.3856 0.585573C20.7606 0.210631 21.2692 0 21.7996 0C22.3299 0 22.8385 0.210631 23.2136 0.585573ZM23.7996 17.7996V12.7996L30.7996 19.7996L23.7996 26.7996V21.7996H17.7996V27.7996H13.7996V19.7996C13.7996 19.2691 14.0103 18.7604 14.3854 18.3854C14.7604 18.0103 15.2691 17.7996 15.7996 17.7996H23.7996Z" fill="#f9f6f2"/></svg></a>
          ${r.url ? `<a class="store-card__link" href="${escapeAttr(r.url)}" target="_blank" rel="noopener noreferrer"><svg width="16" height="16" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M22 0C34.1506 0 44 9.84943 44 22C44 34.1506 34.1506 44 22 44C9.84943 44 0 34.1506 0 22C0 9.84943 9.84943 0 22 0ZM21.5547 32.0049C20.1102 32.0388 18.6775 32.2476 17.2871 32.624C17.5576 33.6301 17.8642 34.5595 18.2002 35.3994C18.8682 37.0693 19.6135 38.2866 20.3389 39.0537C21.0604 39.8166 21.6226 40 22 40C22.3774 40 22.9396 39.8166 23.6611 39.0537C24.3865 38.2866 25.1318 37.0693 25.7998 35.3994C26.1358 34.5596 26.4414 33.63 26.7119 32.624C25.18 32.2093 23.5967 31.9981 22.0029 32H21.9971L21.5547 32.0049ZM13.5488 34.1025C12.5357 34.6416 11.5772 35.277 10.6855 35.999C12.0757 37.124 13.6363 38.0465 15.3232 38.7207C15.0236 38.1391 14.7426 37.5254 14.4863 36.8848C14.1406 36.0205 13.8288 35.0894 13.5488 34.1025ZM30.4502 34.1025C30.1702 35.0893 29.8593 36.0206 29.5137 36.8848C29.2573 37.5256 28.9755 38.139 28.6758 38.7207C30.3628 38.0466 31.9232 37.1239 33.3135 35.999C32.4218 35.277 31.4634 34.6416 30.4502 34.1025ZM4.11133 24C4.49053 27.4303 5.83217 30.5702 7.8623 33.1426C9.3179 31.9196 10.9272 30.8902 12.6504 30.0811C12.3283 28.1671 12.1202 26.1236 12.0391 24H4.11133ZM31.9609 24C31.8798 26.1236 31.6707 28.1671 31.3486 30.0811C33.0719 30.8902 34.681 31.9196 36.1367 33.1426C38.1671 30.5701 39.5094 27.4305 39.8887 24H31.9609ZM16.042 24C16.1101 25.6461 16.2607 27.2212 16.4814 28.6992C18.2789 28.2343 20.1322 27.9982 21.9971 28C23.8635 27.9977 25.7186 28.234 27.5176 28.6992C27.7383 27.2212 27.8899 25.6462 27.958 24H16.042ZM7.8623 10.8564C5.8319 13.4289 4.49056 16.5694 4.11133 20H12.0391C12.1202 17.8761 12.3283 15.8322 12.6504 13.918C10.9271 13.1088 9.31792 12.0795 7.8623 10.8564ZM27.5176 15.2998C25.72 15.7647 23.8669 16.0009 22.002 15.999H22L21.9971 16C20.1321 16.0018 18.279 15.7648 16.4814 15.2998C16.2606 16.7781 16.1101 18.3535 16.042 20H27.958C27.8899 18.3535 27.7384 16.7781 27.5176 15.2998ZM36.1367 10.8564C34.681 12.0795 33.072 13.1088 31.3486 13.918C31.6708 15.8322 31.8798 17.876 31.9609 20H39.8887C39.5094 16.5692 38.1673 13.429 36.1367 10.8564ZM22 4C21.6226 4 21.0604 4.18338 20.3389 4.94629C19.6135 5.71344 18.8682 6.93071 18.2002 8.60059C17.8643 9.44024 17.5575 10.3693 17.2871 11.375C18.8194 11.7899 20.4029 12.0019 21.9971 12H22.0029L22.4453 11.9951C23.8896 11.9612 25.3218 11.7513 26.7119 11.375C26.4415 10.3694 26.1357 9.44018 25.7998 8.60059C25.1318 6.93071 24.3865 5.71344 23.6611 4.94629C22.9396 4.18338 22.3774 4 22 4ZM15.3232 5.27832C13.6363 5.95245 12.0757 6.87515 10.6855 8C11.5772 8.72213 12.5357 9.35739 13.5488 9.89648C13.8287 8.90998 14.1407 7.97922 14.4863 7.11523C14.7427 6.47427 15.0235 5.86018 15.3232 5.27832ZM28.6758 5.27832C28.9757 5.8603 29.2572 6.47412 29.5137 7.11523C29.8592 7.97914 30.1703 8.91008 30.4502 9.89648C31.4634 9.35742 32.4218 8.72211 33.3135 8C31.9233 6.87518 30.3628 5.95238 28.6758 5.27832Z" fill="#f9f6f2"/></svg></a>` : ''}
          ${r.tel ? `<a class="store-card__link${classeTel}" href="tel:${escapeAttr(r.tel)}" rel="nofollow"><svg width="16" height="16" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M29.3755 39.9349C26.4955 39.8289 18.3335 38.7009 9.78548 30.1549C1.23948 21.6069 0.113484 13.4469 0.00548399 10.5649C-0.154516 6.17289 3.20948 1.90689 7.09548 0.240888C7.56344 0.038822 8.07589 -0.0381108 8.58255 0.0176392C9.0892 0.0733892 9.57266 0.259906 9.98548 0.558888C13.1855 2.89089 15.3935 6.41889 17.2895 9.19289C17.7067 9.80235 17.885 10.544 17.7906 11.2765C17.6962 12.009 17.3356 12.6811 16.7775 13.1649L12.8755 16.0629C12.687 16.199 12.5543 16.3989 12.502 16.6255C12.4498 16.8521 12.4816 17.09 12.5915 17.2949C13.4755 18.9009 15.0475 21.2929 16.8475 23.0929C18.6475 24.8929 21.1535 26.5689 22.8715 27.5529C23.0869 27.6738 23.3404 27.7076 23.58 27.6473C23.8196 27.5871 24.0269 27.4373 24.1595 27.2289L26.6995 23.3629C27.1665 22.7426 27.8553 22.327 28.6218 22.2033C29.3883 22.0795 30.173 22.2571 30.8115 22.6989C33.6255 24.6469 36.9095 26.8169 39.3135 29.8949C39.6367 30.3107 39.8424 30.8057 39.9089 31.3282C39.9754 31.8506 39.9003 32.3814 39.6915 32.8649C38.0175 36.7709 33.7815 40.0969 29.3755 39.9349Z" fill="#f9f6f2"/></svg><p>${escapeHtml(r.tel)}</p></a>` : ''}
          ${r.mail ? `<a class="store-card__link${classeMail}" href="mailto:${escapeAttr(r.mail)}" rel="nofollow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6C2 4.89543 2.89543 4 4 4H20C21.1046 4 22 4.89543 22 6V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z" stroke="#f9f6f2" stroke-width="2"/><path d="M3 6.5L12 13L21 6.5" stroke="#f9f6f2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></a>` : ''}
        </footer>
      </article>`;
    }

    /**
     * UTILITÉ : Fabrique le balisage vectoriel SVG du repère géographique de carte (Pin).
     */
    function genererIconeSvgMarqueur(couleur) {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 32 42">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26S32 28 32 16C32 7.163 24.837 0 16 0z"
            fill="${couleur}" stroke="#fff" stroke-width="2"/>
      <circle cx="16" cy="16" r="5.5" fill="#fff" opacity="0.9"/>
    </svg>`;
    }

    /**
     * UTILITÉ : Encapsule la structure de la fiche dans un div d'isolation
     * pour le système d'injection de fenêtres contextuelles (popups) Leaflet.
     */
    function genererEncapsulationPopup(r) {
        const wrapper = document.createElement('div');
        wrapper.className = 'store-popup-content-inner';
        wrapper.innerHTML = genererCardHTML(r);
        return wrapper;
    }

    /**
     * UTILITÉ : Calcule l'aspect visuel de la bulle de regroupement (Cluster).
     */
    function genererStyleIconeCluster(cluster) {
        const count = cluster.getChildCount();
        const classeTaille = count < 5 ? 'small' : count < 10 ? 'medium' : 'large';
        return L.divIcon({
            html: `<div><span>${count}</span></div>`,
            className: `marker-cluster marker-cluster-${classeTaille}`,
            iconSize: L.point(40, 40),
        });
    }

    /**
     * UTILITÉ : Injecte un message d'erreur si le chargement réseau du JSON échoue.
     */
    function afficherMessageErreur() {
        const listWrapper = document.getElementById('store-list');
        const counterEl = document.getElementById('results-counter');
        if (counterEl) counterEl.textContent = 'Erreur';
        if (listWrapper) {
            listWrapper.innerHTML = `
        <li class="points-de-ventes-error-message" style="color:#d93838; padding:20px; list-style:none;">
          ${escapeHtml(TEXTES.erreur_chargement)}
        </li>`;
        }
    }

    function escapeHtml(str) {
        if (str == null) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    function escapeAttr(str) {
        return escapeHtml(str).replace(/"/g, '&quot;');
    }

})();